import GuidePage from "./GuidePage";

const chapters = [
  {
    title: "Vor dem Tag — die Vorbereitung",
    body: (
      <>
        <ul className="space-y-3 list-disc list-inside">
          <li>Lesen Sie die Rede zwei- bis dreimal laut vor sich selbst. Nicht auswendig lernen — vertraut werden.</li>
          <li>Drucken Sie den Text in doppelter Größe (16-18 pt) aus. Am Trauertag lesen Sie ohne Brille schwerer, als Sie denken.</li>
          <li>Machen Sie zwei Ausdrucke: einen für sich, einen in Reserve bei einer Vertrauensperson.</li>
          <li>Markieren Sie sich mit Bleistift Atempausen — kleine Striche zwischen den Sätzen, an denen Sie kurz absetzen dürfen.</li>
          <li>Streichen Sie Wörter, die stolpern. Wenn ein Satz Ihnen beim dritten Lesen unangenehm ist, ändern Sie ihn.</li>
        </ul>
      </>
    ),
  },
  {
    title: "Am Tag selbst — praktische Kleinigkeiten",
    body: (
      <>
        <ul className="space-y-3 list-disc list-inside">
          <li>Wenn möglich, betreten Sie den Ort vorher einmal — die Akustik ist entscheidend für Ihre Stimme.</li>
          <li>Wasser bereitstellen. Ein Schluck kann eine Rede retten.</li>
          <li>Vor dem Aufstehen: drei tiefe Atemzüge. Nicht mehr, nicht weniger.</li>
          <li>Wenn Sie zittern: Halten Sie die Blätter mit beiden Händen an der unteren Kante fest — das dämpft Zittern sichtbar.</li>
          <li>Blick nicht nach unten allein. Suchen Sie eine Person im Raum, die Sie kennen — sprechen Sie zu ihr.</li>
        </ul>
      </>
    ),
  },
  {
    title: "Beim Sprechen — Ton und Tempo",
    body: (
      <>
        <p>
          Das häufigste Problem bei Trauerreden ist nicht Aufregung — es ist <strong>zu schnelles Sprechen</strong>.
          Wenn Sie den Text laut proben, sprechen Sie langsamer als es Ihnen natürlich vorkommt. Am Tag
          selbst wird das Tempo automatisch schneller.
        </p>
        <p>
          Machen Sie <strong>echte Pausen</strong> zwischen den Absätzen — nicht Denkpausen, sondern
          Atempausen. Ein Absatz ist ein Bild, und ein Bild braucht einen Moment, um vom Raum aufgenommen
          zu werden.
        </p>
        <p>
          Wenn Sie eine Stelle besonders ruhig sprechen wollen: Setzen Sie leicht ab, senken Sie die
          Stimme minimal. Das ist wirksamer als Betonung.
        </p>
      </>
    ),
  },
  {
    title: "Was tun, wenn Sie weinen?",
    body: (
      <>
        <p>
          Weinen ist bei einer Trauerrede kein Bruch. Es ist Teil der Rede. Der Raum wird warten.
        </p>
        <p>
          Wenn Sie merken, dass es kommt: Setzen Sie den Satz zu Ende, an dem Sie sind — brechen Sie
          nicht mittendrin ab. Dann Pause. Schluck Wasser. Ein Atemzug. Und weiter.
        </p>
        <p>
          Wenn Sie an einer Stelle wirklich nicht weitermachen können, ist es <strong>vollkommen würdig</strong>,
          zu sagen: „Ich lese den Rest gleich in Ruhe zu Ende“, einen Moment zu warten, und dann fortzufahren.
          Niemand wird das übelnehmen — im Gegenteil.
        </p>
      </>
    ),
  },
  {
    title: "Plan B — jemand anderes vorträgt",
    body: (
      <>
        <p>
          Wenn Sie sich unsicher sind, ob Sie es aushalten werden: Bitten Sie am Vortag eine
          nahestehende Person, den Text zur Not zu übernehmen. Geben Sie ihm oder ihr eine Kopie.
        </p>
        <p>
          Ein guter Trauerrede-Entwurf ist so geschrieben, dass er auch aus einem anderen Mund
          würdig trägt. Nichts ist unwürdiger, als eine Rede abzubrechen, weil kein Plan B da war —
          und nichts würdiger, als eine Rede, die getragen wird, auch wenn sie nicht aus dem eigenen
          Mund kommt.
        </p>
      </>
    ),
  },
  {
    title: "Nach der Rede — der wichtigste Moment",
    body: (
      <>
        <p>
          Setzen Sie sich hin. Atmen Sie. Sie müssen nichts weiter tun.
        </p>
        <p>
          Was Sie gerade getan haben — vor Menschen, die alle in Trauer sind, für einen Menschen,
          den Sie geliebt haben, Worte zu finden und zu halten — ist keine Kleinigkeit. Es ist ein
          Dienst, den nicht viele leisten können.
        </p>
        <p>
          Danken Sie sich selbst, still, im Sitzen. Der Rest der Trauerfeier trägt sich von hier aus.
        </p>
      </>
    ),
  },
];

const faq = [
  {
    q: "Muss ich die Trauerrede auswendig lernen?",
    a: "Nein — im Gegenteil. Lesen Sie sie ab. Auswendigkönnen ist am Trauertag eine unnötige Bürde. Wichtig ist, den Text zwei- bis dreimal laut gelesen zu haben und ihn zu kennen.",
  },
  {
    q: "Was tun, wenn ich beim Sprechen die Tränen nicht halten kann?",
    a: "Setzen Sie den Satz zu Ende, machen Sie eine Pause, trinken Sie einen Schluck Wasser. Der Raum wartet. Weinen ist bei einer Trauerrede kein Bruch — es ist Teil der Rede.",
  },
  {
    q: "Wie stehe ich vor der Rede — Pult, Mikrofon, wo hin schauen?",
    a: "Ein Pult ist eine Stütze — nutzen Sie es, wenn vorhanden. Beim Mikrofon reicht ein Handbreit Abstand. Suchen Sie eine Person im Raum, die Sie kennen, und sprechen Sie zu ihr. Das hilft der Stimme mehr als jede Technik.",
  },
  {
    q: "Was, wenn ich zittere?",
    a: "Halten Sie die Blätter mit beiden Händen an der unteren Kante fest — das dämpft Zittern sichtbar. Und atmen Sie vor dem Beginn drei mal ruhig aus. Zittern ist normal und niemand wird es Ihnen übelnehmen.",
  },
  {
    q: "Wie lange darf ich für Pausen brauchen?",
    a: "So lange Sie brauchen. Pausen sind bei Trauerreden nicht Verlegenheit — sie sind Respekt. Der Raum wird jede Pause tragen, die Sie brauchen.",
  },
];

const related = [
  { label: "Trauerrede schreiben — Anleitung", to: "/trauerrede-schreiben" },
  { label: "Wie lang darf sie sein?", to: "/wie-lang-trauerrede" },
  { label: "Die richtige Einleitung", to: "/trauerrede-einleitung" },
];

export default function TrauerredeHalten() {
  return (
    <GuidePage
      slug="trauerrede-halten"
      metaTitle="Eine Trauerrede halten — Tipps für den Vortrag | Oratiorede"
      metaDescription="Wie hält man eine Trauerrede würdevoll? Praktische Tipps für Vorbereitung, Ton, Pausen — und was tun, wenn die Tränen kommen. Vom Ausdrucken bis zum Nachher."
      eyebrow="Ratgeber · Trauerrede halten"
      headline={
        <>
          Eine Trauerrede halten.
          <br />
          <span className="italic text-umber">Ruhig. Klein. Getragen.</span>
        </>
      }
      intro="Eine Trauerrede zu halten ist keine rhetorische Übung — es ist ein Dienst an einem geliebten Menschen und an dem Raum, der zusammengekommen ist. Hier ist, was hilft, damit dieser Dienst gelingt: Vorbereitung, Tag selbst, Vortrag, und was danach kommt."
      chapters={chapters}
      faq={faq}
      related={related}
    />
  );
}
